import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Plus, Search, FileText, CheckCircle2, XCircle, Clock, Edit, Trash2, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';
import PaginationControls from '../components/PaginationControls';
import { useAuth } from '../AuthContext';
import useAutoRefresh from '../hooks/useAutoRefresh';

const STATUS_BADGES = {
  draft:             { label: 'Draft',            cls: 'bg-slate-100 text-slate-700 border-slate-300' },
  pending_approval:  { label: 'Pending Approval', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  approved:          { label: 'Approved',         cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
  rejected:          { label: 'Rejected',         cls: 'bg-rose-100 text-rose-700 border-rose-300' },
};

const BusinessPlans = () => {
  const { isAdmin, user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [staff, setStaff] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newEntId, setNewEntId] = useState('');
  const [entSearch, setEntSearch] = useState('');
  const [selectedBranchId, setSelectedBranchId] = useState('');
  const [selectedCrpEpId, setSelectedCrpEpId] = useState('');

  const isCRPEP = (user?.role || '') === 'CRP-EP';
  const canApprove = ['admin', 'checker', 'hr', 'account', 'branch'].includes((user?.role || '').toLowerCase());

  // Admin list-page branch filter (separate from CRP-EP's "select branch then entrepreneur" step)
  const [listBranchFilter, setListBranchFilter] = useState('all');

  // Pagination for the business-plans list
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const fetchAll = async (silent = false) => {
    try {
      const bpParams = { skip: page * pageSize, limit: pageSize };
      if (statusFilter !== 'all') bpParams.status = statusFilter;
      if (isAdmin() && listBranchFilter !== 'all') bpParams.branch_id = listBranchFilter;
      const calls = [
        api.get('/business-plans', { params: bpParams }),
        api.get('/entrepreneurs', { params: isCRPEP && selectedBranchId ? { branch_id: selectedBranchId, limit: 5000 } : { limit: 5000 } }),
      ];
      if (branches.length === 0) calls.push(api.get('/branches'));
      // Always (lazy) load staff for CRP-EP picker
      if (staff.length === 0) calls.push(api.get('/staff', { params: { limit: 5000 } }));
      const results = await Promise.all(calls);
      setPlans(results[0].data || []);
      setTotalCount(parseInt(results[0].headers?.['x-total-count'] || '0', 10) || (results[0].data?.length || 0));
      setEntrepreneurs(results[1].data || []);
      let next = 2;
      if (branches.length === 0 && results[next]) { setBranches(results[next].data || []); next++; }
      if (staff.length === 0 && results[next]) { setStaff(results[next].data || []); }
    } catch { if (!silent) toast.error('Failed to load business plans'); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [statusFilter, selectedBranchId, listBranchFilter, page, pageSize]);
  useAutoRefresh(() => fetchAll(true));

  const entById = (id) => entrepreneurs.find(e => e.id === id);

  // Lazy-fetch any entrepreneurs referenced by plans but missing from the bulk list (fixes "Unknown" in reports/lists).
  useEffect(() => {
    if (plans.length === 0) return;
    const knownIds = new Set(entrepreneurs.map(e => e.id));
    const missing = Array.from(new Set(plans.map(p => p.entrepreneur_id).filter(id => id && !knownIds.has(id))));
    if (missing.length === 0) return;
    let cancelled = false;
    (async () => {
      const fetched = [];
      for (const id of missing.slice(0, 50)) {
        try {
          const r = await api.get(`/entrepreneurs/${id}`);
          if (r.data) fetched.push(r.data);
        } catch { /* skip — likely deleted */ }
      }
      if (!cancelled && fetched.length > 0) {
        setEntrepreneurs(prev => {
          const ids = new Set(prev.map(e => e.id));
          return [...prev, ...fetched.filter(e => !ids.has(e.id))];
        });
      }
    })();
    return () => { cancelled = true; };
  }, [plans, entrepreneurs]);

  const filteredPlans = plans.filter(p => {
    if (!search) return true;
    const e = entById(p.entrepreneur_id);
    const q = search.toLowerCase();
    return (e?.name || '').toLowerCase().includes(q)
      || (e?.enrollment_no || '').toLowerCase().includes(q)
      || (e?.enterprise?.name || '').toLowerCase().includes(q);
  });

  const filteredEntForPicker = entrepreneurs
    .filter(e => {
      if (!entSearch) return true;
      const q = entSearch.toLowerCase();
      return (e.name || '').toLowerCase().includes(q)
        || (e.enrollment_no || '').toLowerCase().includes(q)
        || (e.mobile || '').toLowerCase().includes(q);
    })
    .slice(0, 25);

  const handleCreate = async () => {
    if (!newEntId) { toast.error('Pick an entrepreneur'); return; }
    if (isCRPEP && !selectedCrpEpId) { toast.error('Pick a concerned CRP-EP (Step 2)'); return; }
    try {
      const payload = {
        entrepreneur_id: newEntId,
        plan_date: new Date().toISOString().split('T')[0],
      };
      if (selectedCrpEpId) payload.crp_ep_staff_id = selectedCrpEpId;
      const r = await api.post('/business-plans', payload);
      toast.success('Business plan created');
      setShowNew(false); setNewEntId(''); setEntSearch(''); setSelectedCrpEpId('');
      navigate(`/business-plans/${r.data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this business plan?')) return;
    try { await api.delete(`/business-plans/${id}`); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="business-plans-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><FileText className="w-6 h-6 text-emerald-700" />Business Plans</h1>
          <p className="text-sm text-slate-600">Step 2 of 3 → Build a business plan after enrollment, get it approved, then issue the loan.</p>
        </div>
        <Dialog open={showNew} onOpenChange={setShowNew}>
          <DialogTrigger asChild>
            <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="new-business-plan-btn"><Plus className="w-4 h-4 mr-1" />New Plan</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Start a New Business Plan</DialogTitle></DialogHeader>
            <div className="space-y-3">
              {isCRPEP && (
                <div>
                  <Label className="text-xs">Step 1 — Select Branch</Label>
                  <Select value={selectedBranchId} onValueChange={(v) => { setSelectedBranchId(v); setNewEntId(''); }}>
                    <SelectTrigger data-testid="bp-branch-select"><SelectValue placeholder="Choose your branch" /></SelectTrigger>
                    <SelectContent>
                      {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">Pick the branch where the entrepreneur is enrolled</p>
                </div>
              )}
              {isCRPEP && (
                <div>
                  <Label className="text-xs">Step 2 — Concerned CRP-EP</Label>
                  <Select value={selectedCrpEpId} onValueChange={setSelectedCrpEpId} disabled={!selectedBranchId}>
                    <SelectTrigger data-testid="bp-crpep-select"><SelectValue placeholder={selectedBranchId ? 'Choose CRP-EP' : 'Pick a branch first'} /></SelectTrigger>
                    <SelectContent>
                      {staff
                        .filter(s => !selectedBranchId || s.branch_id === selectedBranchId)
                        .filter(s => {
                          const d = (s.designation || s.staff_type || '').toLowerCase();
                          return d.includes('crp') || d.includes('mentor') || d === '';
                        })
                        .map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.name} {s.designation ? `· ${s.designation}` : ''}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-slate-500 mt-1">Select the CRP-EP responsible for this plan</p>
                </div>
              )}
              <div>
                <Label className="text-xs">{isCRPEP ? 'Step 3 — ' : ''}Search Entrepreneur</Label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                  <Input
                    value={entSearch}
                    onChange={e => setEntSearch(e.target.value)}
                    placeholder="Type name, enrollment no, or mobile…"
                    className="pl-8"
                    disabled={isCRPEP && (!selectedBranchId || !selectedCrpEpId)}
                    data-testid="bp-entr-search"
                  />
                </div>
                {isCRPEP && !selectedBranchId ? (
                  <div className="border border-amber-200 bg-amber-50 rounded-md mt-2 px-3 py-2 text-xs text-amber-800">Please select a branch first.</div>
                ) : isCRPEP && !selectedCrpEpId ? (
                  <div className="border border-amber-200 bg-amber-50 rounded-md mt-2 px-3 py-2 text-xs text-amber-800">Please select the concerned CRP-EP.</div>
                ) : (
                  <div className="border border-slate-200 rounded-md mt-2 max-h-48 overflow-y-auto bg-white">
                    {filteredEntForPicker.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-slate-400">No matching entrepreneur. Try fewer letters or check spelling.</div>
                    ) : filteredEntForPicker.map(e => (
                      <button
                        key={e.id}
                        type="button"
                        onClick={() => setNewEntId(e.id)}
                        className={`w-full text-left px-3 py-1.5 text-xs border-b last:border-b-0 hover:bg-emerald-50 ${newEntId === e.id ? 'bg-emerald-100 font-medium' : ''}`}
                        data-testid={`bp-pick-${e.id}`}
                      >
                        {e.enrollment_no || ''} — {e.name} {e.mobile ? <span className="text-slate-400">· {e.mobile}</span> : null}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button onClick={handleCreate} disabled={!newEntId} className="w-full bg-emerald-700 hover:bg-emerald-800 text-white disabled:opacity-50" data-testid="bp-create-confirm">Create &amp; Edit</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <div className="relative md:col-span-2">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by entrepreneur / enrollment / enterprise…" className="pl-10" data-testid="bp-search" />
        </div>
        {isAdmin() && (
          <Select value={listBranchFilter} onValueChange={setListBranchFilter}>
            <SelectTrigger data-testid="bp-branch-filter"><SelectValue placeholder="All Branches" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Branches</SelectItem>
              {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="bp-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="draft">Draft</SelectItem>
            <SelectItem value="pending_approval">Pending Approval</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="rejected">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-700 text-white">
              <tr>
                <th className="px-3 py-2 text-left">Entrepreneur</th>
                <th className="px-3 py-2 text-left">Enterprise</th>
                <th className="px-3 py-2 text-right">Proposed Loan</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredPlans.length === 0 ? (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-slate-400">No business plans yet — click "New Plan" to start.</td></tr>
              ) : filteredPlans.map(p => {
                const e = entById(p.entrepreneur_id);
                const sb = STATUS_BADGES[p.status] || STATUS_BADGES.draft;
                const Icon = p.status === 'approved' ? CheckCircle2 : p.status === 'rejected' ? XCircle : Clock;
                return (
                  <tr key={p.id} className="hover:bg-slate-50">
                    <td className="px-3 py-2">
                      <button onClick={() => navigate(`/business-plans/${p.id}`)} className="text-emerald-700 hover:underline font-medium" data-testid={`bp-row-${p.id}`}>
                        {e?.enrollment_no || '—'} · {e?.name || 'Unknown'}
                      </button>
                    </td>
                    <td className="px-3 py-2">{e?.enterprise?.name || '—'}</td>
                    <td className="px-3 py-2 text-right">Rs.{Number(p.proposed_loan_amount || (Number(p.bank_loan||0) + Number(p.cef_loan||0)) || 0).toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-center"><span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 border rounded-full ${sb.cls}`}><Icon className="w-3 h-3" />{sb.label}</span></td>
                    <td className="px-3 py-2 text-xs text-slate-500">{p.created_at ? new Date(p.created_at).toLocaleDateString('en-IN') : '—'}</td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex gap-1">
                        <button onClick={() => navigate(`/business-plans/${p.id}`)} className="p-1 text-blue-700 hover:bg-blue-50 rounded" title="Open"><Edit className="w-4 h-4" /></button>
                        {p.status === 'approved' && !p.linked_loan_id && (
                          <button onClick={() => { const amt = p.proposed_loan_amount || (Number(p.bank_loan||0) + Number(p.cef_loan||0)); navigate(`/loans?from_plan=${p.id}&entrepreneur_id=${p.entrepreneur_id}&prefill_amount=${amt}&prefill_tenure=${p.repayment_period_months}`); }} className="p-1 text-emerald-700 hover:bg-emerald-50 rounded" title="Issue Loan from this Plan" data-testid={`bp-to-loan-${p.id}`}><ArrowRight className="w-4 h-4" /></button>
                        )}
                        {p.status === 'approved' && p.linked_loan_id && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 border border-purple-300 font-medium" title="Loan already linked">Loan Linked</span>
                        )}
                        {isAdmin() && <button onClick={() => handleDelete(p.id)} className="p-1 text-rose-700 hover:bg-rose-50 rounded" title="Delete"><Trash2 className="w-4 h-4" /></button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <PaginationControls
          page={page}
          pageSize={pageSize}
          totalCount={totalCount}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(0); }}
          label="business plans"
        />
      </div>

      {canApprove && (
        <p className="mt-4 text-xs text-slate-500">As {user?.role}, you can approve or reject plans pending approval. Open a plan in "Pending Approval" status to act on it.</p>
      )}
    </div>
  );
};

export default BusinessPlans;
